import { Select } from "antd";
import { EditOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { listUsers } from "../api/UserClient";
import { User } from "../../types/UserGroup";
import { handleError } from "../ErrorHandling";
import styles from "./OwnerCell.module.less";

interface OwnerCellProps {
    ownerId?: number;
    ownerName?: string;
    canEdit?: boolean;
    onSave?: (newOwnerId: number) => Promise<void>;
    onUpdate?: () => void;
}

export const OwnerCell: React.FC<OwnerCellProps> = ({ ownerId, ownerName, canEdit = true, onSave, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    useEffect(() => {
        if (editing && users.length === 0) {
            loadUsers();
        }
    }, [editing]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const fetchedUsers = await listUsers();
            setUsers(fetchedUsers);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (selectedUserId: number) => {
        setLoading(true);
        try {
            await onSave?.(selectedUserId);
            setEditing(false);
            onUpdate?.();
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const hasOwner = ownerId !== undefined && ownerId !== -1 && ownerName;
    const displayValue = hasOwner ? ownerName : "Set owner";

    return (
        <div className={styles.container}>
            {canEdit && editing ? (
                <Select
                    showSearch
                    autoFocus
                    defaultOpen
                    loading={loading}
                    className={styles.select}
                    placeholder="Select owner"
                    optionFilterProp="label"
                    value={hasOwner ? ownerId : undefined}
                    options={users.map((u) => ({
                        value: u.id,
                        label: u.name,
                    }))}
                    onSelect={handleSelect}
                    onBlur={() => setEditing(false)}
                />
            ) : (
                <button
                    className={`${styles.display} ${!canEdit ? styles.displayDisabled : ""}`}
                    onClick={canEdit ? () => setEditing(true) : undefined}
                >
                    <span className={styles.displayText}>{displayValue}</span>
                    {canEdit && <EditOutlined className={styles.editIcon} />}
                </button>
            )}
        </div>
    );
};
